import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import {
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { getSlotStyle } from './constants';

const SlotCard = ({ slot, onUpdateSlotStatus, onShowClientDetails }) => {
  const style = getSlotStyle(slot.status);
  const isReserved = slot.status === 'reserved';

  // Vérifie si le client est enregistré comme patient ou possède un profil patient
  const hasPatientProfile = slot.patientId || slot.id_patient_profile || slot.is_patient;
  
  // Récupération explicite de l'ID (patient ou client)
  const clientId = slot.patientId || slot.id_patient_profile || slot.clientId || slot.id_client;

  const handleViewClick = (e) => {
    e.stopPropagation();
    if (!onShowClientDetails) return;
    onShowClientDetails(slot);
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '16px',
        border: `1px solid ${style.border}`,
        bgcolor: '#F8FAFC',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        '&:hover': {
          bgcolor: '#FFFFFF',
          borderColor: style.border,
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* HEADER DATE */}
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: style.dot,
          }}
        />
        <Typography variant="body2" fontWeight="800" color="#1E293B">
          {new Date(slot.start_time).toLocaleDateString('fr-FR', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          })}
        </Typography>
      </Box>

      <Divider sx={{ my: 1.5, opacity: 0.5 }} />

      {/* TIME + STATUS */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: isReserved ? 1.5 : slot.blocked_by_google ? 1.5 : 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 900,
            color: style.text,
            bgcolor: style.bg,
            px: 1,
            py: 0.2,
            borderRadius: '6px',
          }}
        >
          {slot.start_time.slice(11, 16)} — {slot.end_time.slice(11, 16)}
        </Typography>

        {/* STATUS SELECT - Désactivé si réservé */}
        <select
          value={slot.status}
          disabled={isReserved}
          onChange={(e) =>
            onUpdateSlotStatus(slot.id_reserved_slot, e.target.value)
          }
          style={{
            border: '1px solid #E2E8F0',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 12,
            outline: 'none',
            cursor: isReserved ? 'not-allowed' : 'pointer',
            backgroundColor: isReserved ? '#F1F5F9' : '#FFFFFF',
            color: isReserved ? '#94A3B8' : '#1E293B',
          }}
        >
          <option value="available">Disponible</option>
          <option value="reserved">Réservé</option>
          <option value="blocked">Bloqué</option>
          <option value="cancelled">Annulé</option>
        </select>
      </Box>

      {/* INFORMATIONS CLIENT / ID VISIBLE */}
      {isReserved && slot.clientName ? (
        <Box
          onClick={handleViewClick}
          sx={{
            mt: 'auto',
            p: 1.2,
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#DBEAFE',
              borderColor: '#93C5FD',
            },
          }}
          title={hasPatientProfile ? "Voir la fiche patient" : "Voir les détails du client"}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1} overflow="hidden">
              <PersonIcon sx={{ fontSize: '1rem', color: '#3B82F6' }} />
              <Box overflow="hidden">
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    {hasPatientProfile ? 'Patient' : 'Client'}
                  </Typography>
                  {/* Affichage de l'ID ici */}
                  {clientId && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        color: '#1D4ED8',
                        bgcolor: '#DBEAFE',
                        px: 0.8,
                        py: 0.1,
                        borderRadius: '4px',
                      }}
                    >
                      #{clientId}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="#1E3A8A"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {slot.clientName}
                </Typography>
              </Box>
            </Box>
            <VisibilityIcon sx={{ fontSize: '1rem', color: '#3B82F6', ml: 1 }} />
          </Box>
        </Box>
      ) : null}

      {/* NOTE DE SYNCHRONISATION GOOGLE CALENDAR */}
      {slot.blocked_by_google && slot.google_event_summary && (
        <Box
          sx={{
            mt: 'auto',
            p: 1,
            borderRadius: '8px',
            bgcolor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#1E40AF',
              fontWeight: 800,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Google Calendar
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#1E293B',
              fontWeight: 600,
              fontSize: '11px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={slot.google_event_summary}
          >
            {slot.google_event_summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SlotCard;